import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, HostBinding, OnDestroy, Renderer2, computed, effect, inject, input, signal } from '@angular/core';
import { FilterSelection } from 'src/app/components/filters/filters.component';
import { GlobalFiltersService } from 'src/app/components/filters/services/global-filters.service';
import { CategoryLayoutData } from 'src/app/core/interfaces/store-configuration.interface';
import { CommunicationService } from 'src/app/core/services/communication/communication.service';
import { DeviceDetectionService } from 'src/app/core/services/device-detection/device-detection.service';
import { createDefaultCategoryLayoutData } from 'src/app/core/utilities/type-defaults.util';
import { MappedFilter } from 'src/app/products/services/filters-adapter.service';
import { FilterManagement } from '../../class/filter-management';


@Component({
  standalone: true,
  selector: 'category-filter-layout-1',
  templateUrl: './category-filter-layout-1.component.html',
  styleUrls: ['./category-filter-layout-1.component.scss'],
  imports: [CommonModule]
})
export class CategoryFilterLayout1Component extends FilterManagement implements AfterViewInit, OnDestroy {
  override globalFiltersService: GlobalFiltersService;
  private renderer = inject(Renderer2);
  private communicationService = inject(CommunicationService);
  private readonly deviceDetectionService = inject(DeviceDetectionService);

  // Signal-based state management
  private _currentAppliedFilters = signal<FilterSelection[] | null>([]);
  private _categoryLayoutData = signal<CategoryLayoutData>(createDefaultCategoryLayoutData());
  private _category = signal<string>('');
  private _availableFilterAttributes = signal<MappedFilter[] | null>([]);
  private _stickyPosition = signal<string>('12px');
  private _firstRender = signal<boolean>(true);
  private _downArrowIndex = signal<number[]>([0]);
  private _upArrowIndex = signal<number[]>([0]);
  private _setTimeout = signal<ReturnType<typeof setTimeout> | null>(null);

  // Readonly signals for template usage
  readonly currentAppliedFiltersSignal = this._currentAppliedFilters.asReadonly();
  readonly categoryLayoutDataSignal = this._categoryLayoutData.asReadonly();
  readonly categorySignal = this._category.asReadonly();
  readonly availableFilterAttributesSignal = this._availableFilterAttributes.asReadonly();
  readonly stickyPositionSignal = this._stickyPosition.asReadonly();
  readonly firstRenderSignal = this._firstRender.asReadonly();
  readonly downArrowIndexSignal = this._downArrowIndex.asReadonly();
  readonly upArrowIndexSignal = this._upArrowIndex.asReadonly();

  // Computed signals for derived state
  readonly hasCurrentAppliedFilters = computed(() =>
    (this._currentAppliedFilters()?.length ?? 0) > 0
  );
  readonly hasAvailableFilterAttributes = computed(() =>
    (this._availableFilterAttributes()?.length ?? 0) > 0
  );
  readonly hasCategory = computed(() => !!this._category());

  currentAppliedFilters = input<FilterSelection[] | null>([]);
  categoryLayoutData = input<CategoryLayoutData>(createDefaultCategoryLayoutData());
  category = input<string>('');
  availableFilterAttributes = input<MappedFilter[] | null>([]);

  // Effects to handle input changes
  private currentAppliedFiltersEffect = effect(() => {
    const value = this.currentAppliedFilters();
    this._currentAppliedFilters.set(value);
  });

  private categoryLayoutDataEffect = effect(() => {
    const value = this.categoryLayoutData();
    this._categoryLayoutData.set(value);
  });

  private categoryEffect = effect(() => {
    const value = this.category();
    if (value) {
      this._firstRender.set(true);
      this._downArrowIndex.set([]);
      this._upArrowIndex.set([0]);
      this._category.set(value);
    }
  });

  private availableFilterAttributesEffect = effect(() => {
    const value = this.availableFilterAttributes();
    if (value?.length && this._firstRender()) {
      const downIndices: number[] = [];
      value.forEach((_, i) => {
        if (i > 0) downIndices.push(i);
      });
      this._downArrowIndex.set(downIndices);
      this._firstRender.set(false);
    }
    this._availableFilterAttributes.set(value);
  });

  // Effect to watch header reference changes for sticky positioning
  private headerRefEffect = effect(() => {
    const header = this.communicationService.headerRef();
    if (header) {
      this.getHeaderHeightDetails();
    }
  });

  // Effect to watch sticky header changes for positioning
  private stickyHeaderEffect = effect(() => {
    const stickyHeader = this.communicationService.stickyHeader();
    if (stickyHeader && !this.deviceDetectionService.isMobile()) {
      this.getHeaderHeightDetails();
    }
  });

  @HostBinding('style.--sticky-position') get stickyPositionBinding() {
    return this._stickyPosition();
  }

  constructor() {
    const globalFiltersService = inject(GlobalFiltersService);
    super(globalFiltersService);
    this.globalFiltersService = globalFiltersService;
  }

  ngAfterViewInit(): void {
    // Initial header height calculation
    this.getHeaderHeightDetails();
  }

  getHeaderHeightDetails() {
    if (this.communicationService.stickyHeader()) {
      const header = this.communicationService.headerRef();
      if (header) {
        const headerHeight = header?.elementRef?.nativeElement?.offsetHeight || 0;
        this._stickyPosition.set(`${headerHeight + 12}px`);
      }
    }
  }

  openFilterDropdown(index: number) {
    const currentUpIndices = this._upArrowIndex();
    if (!currentUpIndices.includes(index)) {
      this._upArrowIndex.set([...currentUpIndices, index]);
    }

    const currentDownIndices = this._downArrowIndex();
    this._downArrowIndex.set(currentDownIndices.filter(i => i !== index));
  }

  closeFilterDropdown(index: number) {
    const currentDownIndices = this._downArrowIndex();
    if (!currentDownIndices.includes(index)) {
      this._downArrowIndex.set([...currentDownIndices, index]);
    }

    const currentUpIndices = this._upArrowIndex();
    this._upArrowIndex.set(currentUpIndices.filter(i => i !== index));
  }

  handleMenuMouseOut(event: MouseEvent): void {
    const targetElement = event.target as HTMLElement;
    this.renderer.removeStyle(targetElement, 'color');
    this.renderer.removeStyle(targetElement, 'background-color');

    const timeoutId = setTimeout(() => {
      const menuItemToActivate = document.getElementsByClassName('hover-color-active');
      const parentElementMenuItemToActivate = menuItemToActivate[0]?.parentElement;

      if (menuItemToActivate && menuItemToActivate.length > 0) {
        for (let i = 0; i < menuItemToActivate.length; i++) {
          const backgroundColor = this._categoryLayoutData()?.filterData?.styleConfiguration?.desktopStyleConfiguration?.checkBoxBackgroundColor;
          if (backgroundColor) {
            this.renderer.setStyle(menuItemToActivate[i], 'background-color', backgroundColor);
          }
        }
      }

      const menuItemToDeActivate = document.getElementsByClassName('hover-color-inactive');
      if (menuItemToDeActivate.length > 0) {
        for (let i = 0; i < menuItemToDeActivate.length; i++) {
          this.renderer.removeStyle(menuItemToDeActivate[i], 'color');
          this.renderer.removeStyle(menuItemToDeActivate[i], 'background-color');
        }
      }
    }, 10);

    this._setTimeout.set(timeoutId);
  }

  ngOnDestroy(): void {
    const timeoutId = this._setTimeout();
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
}


